/**
 * Standalone API client for care pass delegates.
 *
 * Uses sessionStorage (not localStorage) for the delegate JWT,
 * so closing the tab clears the session. Does NOT use the auth store.
 */

import axios from 'axios';
import i18n from '../i18n';
import { serverErrorText } from './errors';
// Types imported from main api when needed for cross-referencing
import type { WeightDisplayUnit } from '../components/weight/types';

const DELEGATE_TOKEN_KEY = 'ckd_delegate_token';

const client = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Attach delegate JWT on every request
client.interceptors.request.use((config) => {
  const token = sessionStorage.getItem(DELEGATE_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Why a care pass could not be used. `revoked` and `expired` are ordinary,
 * expected outcomes with their own screen; anything else is a genuine fault and
 * keeps the generic message.
 */
export type CarePassErrorCode =
  | 'revoked'
  | 'expired'
  | 'not_started'
  | 'inactive'
  | 'not_found';

export interface CarePassError extends Error {
  code?: CarePassErrorCode;
}

const CARE_PASS_ERROR_CODES: CarePassErrorCode[] = [
  'revoked', 'expired', 'not_started', 'inactive', 'not_found',
];

/** Build an Error from a care-pass refusal body, carrying its `code` when present. */
function carePassError(body: ErrorBody | undefined): CarePassError {
  const err: CarePassError = new Error(serverErrorText(body, i18n.t('carePass.api.accessDenied')));
  const detail = body?.detail;
  if (detail && typeof detail === 'object') {
    const { code } = detail as { code?: unknown };
    if (typeof code === 'string' && CARE_PASS_ERROR_CODES.includes(code as CarePassErrorCode)) {
      err.code = code as CarePassErrorCode;
    }
  }
  return err;
}

/** An error body: the English `detail`, and the `code` and `params` it is rendered from. */
interface ErrorBody {
  detail?: unknown;
  code?: unknown;
  params?: unknown;
}

/**
 * Map an axios failure to the Error the caregiver screens work with.
 * Exported (as `handleApiResponseError` is on the owner client) so the mapping
 * can be unit-tested without standing up a server.
 */
export function handleCareApiResponseError(error: {
  response?: { status?: number; data?: ErrorBody };
}): Promise<never> {
  if (error.response?.status === 403 || error.response?.status === 404) {
    // Care-pass refusals carry { code, message }: a revoked or expired pass is
    // an expected authorisation outcome and the caregiver's screen renders its
    // own state for it, rather than printing a message it can't interpret.
    // Older bodies were a bare string, so both shapes are accepted.
    return Promise.reject(carePassError(error.response?.data));
  }
  if (error.response?.status === 409) {
    // Canonical 409 body: { message, existing_id }. Carry status + existingLogId
    // on the thrown Error so the shared DailyLogForm can offer an update (via the
    // delegate PUT) instead of dead-ending, exactly like the owner path does.
    const detail = error.response?.data?.detail as
      | string
      | { message?: string; existing_id?: string; existing_log_id?: string };
    const err = new Error(serverErrorText(error.response?.data, i18n.t('carePass.api.entryExists'))) as Error & {
      status?: number;
      existingLogId?: string | null;
    };
    err.status = 409;
    err.existingLogId =
      (typeof detail === 'object' ? detail.existing_id ?? detail.existing_log_id : null) ?? null;
    return Promise.reject(err);
  }
  if (error.response?.status && error.response.status >= 500) {
    return Promise.reject(new Error(i18n.t('carePass.api.serverError')));
  }
  if (!error.response) {
    return Promise.reject(new Error(i18n.t('carePass.api.networkError')));
  }
  return Promise.reject(new Error(serverErrorText(error.response?.data, i18n.t('carePass.api.genericError'))));
}

// Response interceptor: user-friendly error messages
client.interceptors.response.use((response) => response, handleCareApiResponseError);

// ────────────────────────────────────────
// Types
// ────────────────────────────────────────

export interface CarePassLanding {
  cat_name: string;
  /** The cat's sex, so Portuguese text can agree with it ("o peso da Luna"). */
  cat_sex?: 'male' | 'female' | null;
  cat_id: string;
  /** This pass's id. The check-in compares it against each care event's
   *  `care_pass_id` to decide which events this delegate may remove. */
  care_pass_id: string;
  delegate_name: string;
  scopes: string[];
  starts_at: string;
  ends_at: string;
  access_token: string;
  /** The OWNER's display unit. The caregiver enters in the unit the owner speaks,
   *  so "she's about 10.6" cannot land in a kg-only field as a 23 lb cat. */
  weight_unit: WeightDisplayUnit;
}

export interface CarePassMedication {
  id: string;
  drug_name: string;
  dose: number | null;
  dose_unit: string | null;
  frequency: string;
  route: string;
  category: string;
  notes: string | null;
}

export interface CarePassDailyLog {
  id: string;
  cat_id: string;
  date: string;
  appetite_score: number | null;
  energy_level: number | null;
  water_intake_observation: string | null;
  vomiting: boolean | null;
  vomiting_count: number | null;
  defecation: boolean | null;
  defecation_count: number | null;
  medications_given: Record<string, boolean> | null;
  subq_fluids_administered: boolean | null;
  subq_fluids_volume_ml: number | null;
  notes: string | null;
  care_pass_id: string | null;
  created_at: string;
}

export interface CarePassCareEvent {
  id: string;
  cat_id: string;
  date: string;
  occurred_at: string | null;
  event_type: 'subq_fluids' | 'vomiting' | 'defecation' | 'medication_given' | 'medication_skipped' | 'glucose' | 'urination';
  detail: Record<string, unknown>;
  medication_id: string | null;
  care_pass_id: string | null;
  delegate_name: string | null;
  created_at: string;
  updated_at: string;
}

/** One rotation site's last-used facts (GET /care/:token/subq-fluids/site-history).
 *  Identical shape to the owner read — the backend serves both from one aggregation. */
export interface CarePassSubqSiteHistoryEntry {
  site: string;
  last_used_date: string | null;
  last_used_at: string | null;
  session_count: number;
}

// ────────────────────────────────────────
// API methods
// ────────────────────────────────────────

export const careApi = {
  /**
   * Validate a care pass URL token. On success, stores the delegate JWT
   * in sessionStorage and returns the landing data.
   */
  async validateToken(urlToken: string): Promise<CarePassLanding> {
    const response = await client.get(`/care/${urlToken}`);
    const data = response.data as CarePassLanding;
    sessionStorage.setItem(DELEGATE_TOKEN_KEY, data.access_token);
    return data;
  },

  /**
   * Submit a daily log as a delegate.
   */
  async submitDailyLog(urlToken: string, data: Record<string, unknown>): Promise<CarePassDailyLog> {
    const response = await client.post(`/care/${urlToken}/daily-log`, data);
    return response.data as CarePassDailyLog;
  },

  /**
   * Correct a daily log the delegate previously submitted (same-day 409 recovery).
   * Routes to the delegate PUT, which applies the same partial-merge (exclude_unset)
   * semantics as the owner endpoint — only the sent fields change.
   */
  async updateDailyLog(
    urlToken: string,
    logId: string,
    data: Record<string, unknown>,
  ): Promise<CarePassDailyLog> {
    const response = await client.put(`/care/${urlToken}/daily-log/${logId}`, data);
    return response.data as CarePassDailyLog;
  },

  /**
   * Submit a weight entry as a delegate.
   */
  async submitWeight(urlToken: string, data: { date: string; weight_kg: number; measurement_method?: string }): Promise<unknown> {
    const response = await client.post(`/care/${urlToken}/weight`, data);
    return response.data;
  },

  /**
   * Record a care event as a delegate (SubQ session, vomiting, meds given, etc.).
   * A second same-day event is a new row — never a dead-end (ADR C9).
   */
  async submitCareEvent(urlToken: string, data: Record<string, unknown>): Promise<unknown> {
    const response = await client.post(`/care/${urlToken}/care-event`, data);
    return response.data;
  },

  /**
   * Log a SubQ fluid session as a delegate (dedicated "log fluids" permission).
   * A second same-day session is a new row — never a dead-end (ADR C9).
   */
  async submitSubqFluids(urlToken: string, data: Record<string, unknown>): Promise<unknown> {
    const response = await client.post(`/care/${urlToken}/subq-fluids`, data);
    return response.data;
  },

  /**
   * Per-site last-used facts for the sub-Q rotation, under the pass's own
   * `log_fluids` permission. Same aggregation the owner's screen reads, so the
   * caregiver's site map carries the real history and the same suggested site.
   */
  async getSubqSiteHistory(urlToken: string): Promise<CarePassSubqSiteHistoryEntry[]> {
    const response = await client.get(`/care/${urlToken}/subq-fluids/site-history`);
    const sites = (response.data as { sites?: CarePassSubqSiteHistoryEntry[] })?.sites;
    return Array.isArray(sites) ? sites : [];
  },

  /** The cat's care events, optionally for a single date. A delegate sees the whole
   *  day (including the owner's events) so the check-in can show what has already
   *  been recorded; removal is still restricted server-side to their own events. */
  async getCareEvents(urlToken: string, date?: string): Promise<CarePassCareEvent[]> {
    const response = await client.get(`/care/${urlToken}/care-events`, {
      params: date ? { date } : undefined,
    });
    return Array.isArray(response.data) ? (response.data as CarePassCareEvent[]) : [];
  },

  /** Remove a care event the delegate previously submitted. */
  async deleteCareEvent(urlToken: string, eventId: string): Promise<void> {
    await client.delete(`/care/${urlToken}/care-event/${eventId}`);
  },

  /**
   * Get active medications (read-only).
   */
  async getMedications(urlToken: string): Promise<CarePassMedication[]> {
    const response = await client.get(`/care/${urlToken}/medications`);
    return response.data as CarePassMedication[];
  },

  /**
   * Get the last 3 daily logs for continuity context.
   */
  async getRecentLogs(urlToken: string): Promise<CarePassDailyLog[]> {
    const response = await client.get(`/care/${urlToken}/recent-logs`);
    return response.data as CarePassDailyLog[];
  },

  /**
   * Clear the delegate session.
   */
  clearSession(): void {
    sessionStorage.removeItem(DELEGATE_TOKEN_KEY);
  },
};
